import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AnnotationToolkit = ({ annotations, onAddAnnotation, onDeleteAnnotation }) => {
  const [activeTool, setActiveTool] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [measurementValue, setMeasurementValue] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');

  const annotationTools = [
    { id: 'marker', label: 'Marker', icon: 'MapPin', color: 'red' },
    { id: 'measurement', label: 'Measure', icon: 'Ruler', color: 'yellow' },
    { id: 'note', label: 'Note', icon: 'MessageSquare', color: 'blue' },
    { id: 'arrow', label: 'Arrow', icon: 'ArrowRight', color: 'green' },
    { id: 'circle', label: 'Circle', icon: 'Circle', color: 'purple' },
    { id: 'rectangle', label: 'Box', icon: 'Square', color: 'orange' }
  ];

  const dentalSymbols = [
    { value: 'caries', label: 'Caries', icon: 'AlertCircle' },
    { value: 'crown', label: 'Crown', icon: 'Award' },
    { value: 'filling', label: 'Filling', icon: 'Disc' },
    { value: 'root_canal', label: 'Root Canal', icon: 'GitBranch' },
    { value: 'implant', label: 'Implant', icon: 'Anchor' },
    { value: 'extraction', label: 'Extraction', icon: 'X' },
    { value: 'fracture', label: 'Fracture', icon: 'Zap' },
    { value: 'abscess', label: 'Abscess', icon: 'AlertTriangle' }
  ];

  const handleAddMarker = () => {
    onAddAnnotation({
      type: 'marker',
      x: 50,
      y: 50,
      color: 'red',
      symbol: selectedSymbol
    });
    setActiveTool(null);
  };

  const handleAddMeasurement = () => {
    if (measurementValue) {
      onAddAnnotation({
        type: 'measurement',
        x: 45,
        y: 45,
        value: measurementValue,
        unit: 'mm'
      });
      setMeasurementValue('');
      setActiveTool(null);
    }
  };

  const handleAddNote = () => {
    if (noteText) {
      onAddAnnotation({
        type: 'note',
        x: 40,
        y: 40,
        text: noteText
      });
      setNoteText('');
      setActiveTool(null);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Edit3" size={16} />
            Annotation Tools
          </h3>
          <Button
            variant="ghost"
            size="xs"
            iconName="Trash2"
            onClick={() => annotations?.forEach(a => onDeleteAnnotation(a?.id))}
            disabled={annotations?.length === 0}
          >
            Clear All
          </Button>
        </div>

        {/* Tool Selection */}
        <div className="grid grid-cols-3 gap-2">
          {annotationTools?.map((tool) => (
            <Button
              key={tool?.id}
              variant={activeTool === tool?.id ? 'default' : 'outline'}
              size="sm"
              iconName={tool?.icon}
              onClick={() => setActiveTool(tool?.id)}
              className="flex-col h-auto py-2"
            >
              <span className="text-xs mt-1">{tool?.label}</span>
            </Button>
          ))}
        </div>
      </div>
      {/* Tool Options */}
      {activeTool && (
        <div className="p-4 border-b border-border bg-muted/30">
          {activeTool === 'marker' && (
            <div className="space-y-3">
              <Select
                label="Dental Symbol"
                placeholder="Select symbol"
                value={selectedSymbol}
                onChange={setSelectedSymbol}
                options={dentalSymbols?.map(s => ({
                  value: s?.value,
                  label: s?.label
                }))}
              />
              <Button
                variant="default"
                size="sm"
                fullWidth
                onClick={handleAddMarker}
              >
                Add Marker
              </Button>
            </div>
          )}

          {activeTool === 'measurement' && (
            <div className="space-y-3">
              <Input
                label="Measurement (mm)"
                type="number"
                placeholder="Enter value"
                value={measurementValue}
                onChange={(e) => setMeasurementValue(e?.target?.value)}
              />
              <Button
                variant="default"
                size="sm"
                fullWidth
                onClick={handleAddMeasurement}
                disabled={!measurementValue}
              >
                Add Measurement
              </Button>
            </div>
          )}

          {activeTool === 'note' && (
            <div className="space-y-3">
              <Input
                label="Annotation Note"
                placeholder="Enter note"
                value={noteText}
                onChange={(e) => setNoteText(e?.target?.value)}
              />
              <Button
                variant="default"
                size="sm"
                fullWidth
                onClick={handleAddNote}
                disabled={!noteText}
              >
                Add Note
              </Button>
            </div>
          )}

          {['arrow', 'circle', 'rectangle']?.includes(activeTool) && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-3">
                Click on the image to place {activeTool}
              </p>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => setActiveTool(null)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Annotations List */}
      <div className="p-4">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
          Active Annotations ({annotations?.length})
        </h4>
        
        {annotations?.length === 0 ? (
          <div className="text-center py-6">
            <Icon name="MessageSquare" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No annotations yet
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Select a tool above to start
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {annotations?.map((annotation, index) => (
              <div
                key={annotation?.id}
                className="flex items-start justify-between p-2 bg-muted/50 rounded border border-border hover:bg-muted transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon
                      name={
                        annotation?.type === 'marker' ? 'MapPin' :
                        annotation?.type === 'measurement' ? 'Ruler' :
                        annotation?.type === 'note'? 'MessageSquare' : 'Circle'
                      }
                      size={14}
                      className="text-primary"
                    />
                    <span className="text-xs font-medium text-foreground capitalize">
                      {annotation?.type}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {annotation?.type === 'measurement' && `${annotation?.value}mm`}
                    {annotation?.type === 'note' && annotation?.text}
                    {annotation?.type === 'marker' && (annotation?.symbol || 'Marker')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Trash2"
                  onClick={() => onDeleteAnnotation(annotation?.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnotationToolkit;